/**
 * DEV-ONLY test harness for notification UI screenshots.
 * Accessible at /__tests/notifications when running in development.
 *
 * Each notification type is rendered inside a fixed-width card so that
 * Playwright snapshot tests produce stable, deterministic screenshots.
 */
import { NotificationTypeId } from '../../components/Header/Notification/constants/notification-types';
import NotificationTypeRenderer from '../../components/Header/Notification/NotificationTypeRenderer';

/* ─── Fixture data ──────────────────────────────────────────────────────────── */

const FIXTURES = {
  /** NEWSLETTER_ARTICLE_LIKE (8) */
  'like-article': {
    id: 'fix-like-article',
    notificationTypeId: NotificationTypeId.NEWSLETTER_ARTICLE_LIKE,
    markAsRead: false,
    metadata: {
      firstName: 'Sam',
      lastName: 'Rivera',
      avatar: 'https://i.pravatar.cc/150?img=1',
      reactionType: 'like',
      articleTitle: 'The Future of AI in Healthcare'
    }
  },

  /** NEWSLETTER_COMMENT_LIKE (11) */
  'like-comment': {
    id: 'fix-like-comment',
    notificationTypeId: NotificationTypeId.NEWSLETTER_COMMENT_LIKE,
    markAsRead: false,
    metadata: {
      firstName: 'Dana',
      lastName: 'Quinn',
      avatar: 'https://i.pravatar.cc/150?img=2',
      reactionType: 'love',
      comment: 'Great perspective on the topic!',
      articleTitle: 'The Future of AI in Healthcare'
    }
  },

  /** NEWSLETTER_USER_FOLLOW (13) */
  follow: {
    id: 'fix-follow',
    notificationTypeId: NotificationTypeId.NEWSLETTER_USER_FOLLOW,
    markAsRead: false,
    metadata: {
      firstName: 'Alice',
      lastName: 'Jones',
      avatar: 'https://i.pravatar.cc/150?img=5'
    }
  },

  /** NEWSLETTER_ARTICLE_INVESTMENT (16) */
  investment: {
    id: 'fix-investment',
    notificationTypeId: NotificationTypeId.NEWSLETTER_ARTICLE_INVESTMENT,
    markAsRead: false,
    metadata: {
      firstName: 'Alex',
      lastName: 'Rivera',
      avatar: 'https://i.pravatar.cc/150?img=33',
      pointInvested: 250,
      articleTitle: 'Why Does My Dog Snore During Meetings?'
    }
  },

  /** NEWSLETTER_ARTICLE_COMMENT (9) */
  'article-comment': {
    id: 'fix-article-comment',
    notificationTypeId: NotificationTypeId.NEWSLETTER_ARTICLE_COMMENT,
    markAsRead: false,
    metadata: {
      firstName: 'Lee',
      lastName: 'Kim',
      avatar: 'https://i.pravatar.cc/150?img=8',
      comment: 'This article changed my perspective entirely.',
      articleTitle: 'Building Scalable Systems'
    }
  },

  /** NEWSLETTER_ARTICLE_SECTION_COMMENT (10) */
  'section-comment': {
    id: 'fix-section-comment',
    notificationTypeId: NotificationTypeId.NEWSLETTER_ARTICLE_SECTION_COMMENT,
    markAsRead: false,
    metadata: {
      firstName: 'Priya',
      lastName: 'Singh',
      avatar: 'https://i.pravatar.cc/150?img=9',
      comment: 'Loved the section on microservices.',
      articleTitle: 'Building Scalable Systems'
    }
  },

  /** NEWSLETTER_COMMENT_REPLY (12) */
  'comment-reply': {
    id: 'fix-comment-reply',
    notificationTypeId: NotificationTypeId.NEWSLETTER_COMMENT_REPLY,
    markAsRead: false,
    metadata: {
      firstName: 'Noah',
      lastName: 'Baker',
      avatar: 'https://i.pravatar.cc/150?img=11',
      comment: 'Totally agree with your point!',
      articleTitle: 'The Future of AI in Healthcare'
    }
  },

  /** NEWSLETTER_COMMENT_MENTION (18) */
  'comment-mention': {
    id: 'fix-comment-mention',
    notificationTypeId: NotificationTypeId.NEWSLETTER_COMMENT_MENTION,
    markAsRead: false,
    metadata: {
      firstName: 'Mia',
      lastName: 'Torres',
      avatar: 'https://i.pravatar.cc/150?img=16',
      comment: '@you should check this out!',
      articleTitle: 'Open Source in 2025'
    }
  },

  /** NEWSLETTER_ARTICLE_MENTION (19) */
  'article-mention': {
    id: 'fix-article-mention',
    notificationTypeId: NotificationTypeId.NEWSLETTER_ARTICLE_MENTION,
    markAsRead: false,
    metadata: {
      firstName: 'Chris',
      lastName: 'Park',
      avatar: 'https://i.pravatar.cc/150?img=18',
      articleTitle: 'Developer Productivity Tools'
    }
  },

  /** NEWSLETTER_CONTENT_FLAGGED (20) */
  flagged: {
    id: 'fix-flagged',
    notificationTypeId: NotificationTypeId.NEWSLETTER_CONTENT_FLAGGED,
    markAsRead: false,
    metadata: {
      contentType: 'article',
      reason: 'potentially inappropriate content',
      articleTitle: 'Controversial Tech Take'
    }
  },

  /** NEWSLETTER_CONTENT_MODERATED (21) */
  moderated: {
    id: 'fix-moderated',
    notificationTypeId: NotificationTypeId.NEWSLETTER_CONTENT_MODERATED,
    markAsRead: false,
    metadata: {
      contentType: 'comment',
      reason: 'violates community guidelines',
      comment: 'Removed comment text.',
      articleTitle: 'Controversial Tech Take'
    }
  },

  /** ARTICLE_MILESTONE_1000 (27) */
  milestone: {
    id: 'fix-milestone',
    notificationTypeId: NotificationTypeId.ARTICLE_MILESTONE_1000,
    markAsRead: false,
    metadata: {
      articleMilestone: 10000,
      articleTitle: 'Why Does My Dog Snore During Meetings?',
      articleViewNumber: 10247,
      articleReactionNumber: 312,
      articleCommentNumber: 88
    }
  },

  /** NEWSLETTER_REPUTATION_POINTS (17) */
  reputation: {
    id: 'fix-reputation',
    notificationTypeId: NotificationTypeId.NEWSLETTER_REPUTATION_POINTS,
    markAsRead: false,
    metadata: {
      firstName: 'Sarah',
      lastName: 'Kim',
      avatar: 'https://i.pravatar.cc/150?img=22',
      twigsAmount: 50,
      twigsAwardEntityType: 'article',
      articleTitle: 'The Secret Life of Office Plants'
    }
  },

  /** NEWSLETTER_CREATE_RESPONSE (14) */
  'review-create': {
    id: 'fix-review-create',
    notificationTypeId: NotificationTypeId.NEWSLETTER_CREATE_RESPONSE,
    markAsRead: false,
    metadata: {
      firstName: 'Maria',
      lastName: 'Jones',
      avatar: 'https://i.pravatar.cc/150?img=47',
      requestTitle: "I'm Sorry My Cat is Not Professional"
    }
  },

  /** NEWSLETTER_UPDATE_RESPONSE (15) */
  'review-update': {
    id: 'fix-review-update',
    notificationTypeId: NotificationTypeId.NEWSLETTER_UPDATE_RESPONSE,
    markAsRead: false,
    metadata: {
      firstName: 'Charlie',
      lastName: 'Green',
      avatar: 'https://i.pravatar.cc/150?img=12',
      responseType: 'APPROVED',
      responseTitle: 'Why Does My Dog Snore During Meetings?',
      requestTitle: 'The Great Remote Work Experiment'
    }
  },

  /** NEWSLETTER_ARTICLE_REVIEW (7) */
  'article-review': {
    id: 'fix-article-review',
    notificationTypeId: NotificationTypeId.NEWSLETTER_ARTICLE_REVIEW,
    markAsRead: false,
    metadata: {
      description: 'Your article is under editorial review.',
      articleTitle: 'Advanced TypeScript Patterns'
    }
  },

  /** CREATOR_PAYOUT_THRESHOLD (22) */
  'payout-creator': {
    id: 'fix-payout-creator',
    notificationTypeId: NotificationTypeId.CREATOR_PAYOUT_THRESHOLD,
    markAsRead: false,
    metadata: {
      description: 'You have reached the payout threshold. Your earnings are ready!'
    }
  },

  /** ADMIN_CREATOR_PAYOUT_THRESHOLD (23) */
  'payout-admin': {
    id: 'fix-payout-admin',
    notificationTypeId: NotificationTypeId.ADMIN_CREATOR_PAYOUT_THRESHOLD,
    markAsRead: false,
    metadata: {
      description: '[Admin] Creator Jane Doe has reached the payout threshold.'
    }
  },

  /** Unread variant – markAsRead=true shows white BG; markAsRead=false shows green BG */
  'like-article-unread': {
    id: 'fix-like-article-unread',
    notificationTypeId: NotificationTypeId.NEWSLETTER_ARTICLE_LIKE,
    markAsRead: false,
    metadata: {
      firstName: 'Sam',
      lastName: 'Rivera',
      avatar: 'https://i.pravatar.cc/150?img=1',
      reactionType: 'like',
      articleTitle: 'The Future of AI in Healthcare'
    }
  }
};

/* ─── Helper ────────────────────────────────────────────────────────────────── */

const NotificationCard = ({ testId, notification }) => (
  <div
    data-testid={`notification-${testId}`}
    className={`w-[480px] rounded-md p-3 border border-gray-200 ${notification.markAsRead ? 'bg-white' : 'bg-[#EDF7EE]'}`}
  >
    <NotificationTypeRenderer
      notification={notification}
      displayName={
        notification.metadata?.firstName
          ? `${notification.metadata.firstName} ${notification.metadata.lastName ?? ''}`.trim()
          : 'System'
      }
      subIcon={notification.subIcon}
    />
  </div>
);

/* ─── Page ──────────────────────────────────────────────────────────────────── */

const NotificationsTestPage = () => (
  <div className="min-h-screen bg-gray-50 p-8">
    <h1 className="text-2xl font-bold mb-8 text-gray-800">Notifications test page (dev only)</h1>

    <div className="flex flex-col gap-4">
      {Object.entries(FIXTURES).map(([key, notification]) => (
        <div key={key}>
          <p className="text-xs text-gray-400 mb-1 font-mono">
            type: {notification.notificationTypeId} | key: {key}
          </p>
          <NotificationCard testId={key} notification={notification} />
        </div>
      ))}
    </div>
  </div>
);

export default NotificationsTestPage;
